import { useTranslation } from '@rhino-project/core/i18n';
import { useEffect, useState } from 'react';

import { useUserId } from '@rhino-project/core/hooks';
import { useBaseOwnerId } from '@rhino-project/core/hooks';
import { BaseAuthedPage } from '@rhino-project/ui-heroui';
import {
  AlertDialog,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectIndicator,
  SelectPopover,
  ListBox,
  ListBoxItem,
  Dropdown,
  DropdownTrigger,
  DropdownPopover,
  DropdownMenu,
  DropdownItem
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { ModelIndexSimple } from '@rhino-project/ui-heroui';
import { useModelIndexContext } from '@rhino-project/core/hooks';
import Scout from '../../components/scout/Scout';
import { useFocusMode } from '../../hooks/useFocusMode';
import { getLocalTimeZone, today } from '@internationalized/date';

type ObservationData = {
  id: number | string;
  display_name: string;
  [key: string]: unknown;
};

type ObservationPickerProps = {
  observation: ObservationData | null;
  setObservation: (obs: ObservationData | null) => void;
};

const ObservationPicker = ({
  observation,
  setObservation
}: ObservationPickerProps) => {
  const { t } = useTranslation();

  const organization = useBaseOwnerId();
  const { focus, toggle } = useFocusMode();
  const {
    results: observations,
    create: { mutate: createObservation },
    destroy: { mutate: destroyObservation, isPending: isDeleting }
  } = useModelIndexContext();

  useEffect(() => {
    const observationFound = observations?.find(
      (o: ObservationData) => o.id === observation?.id
    );

    if (!observation || (observation && !observationFound))
      setObservation(observations?.[0] || null);
  }, [observation, observations, setObservation]);

  const handleNewClick = () =>
    createObservation(
      {
        organization,
        observed_at: today(getLocalTimeZone()).toString(),
        report: {
          teams: [
            {
              name: t('frontend.messages.home_team'),
              external_linked_team: null,
              external_team: null,
              season: null,
              players: []
            },
            {
              name: t('frontend.messages.visitor_team'),
              external_linked_team: null,
              external_team: null,
              season: null,
              players: []
            }
          ]
        }
      },
      {
        onSuccess: (newObservation: ObservationData) =>
          setObservation(newObservation)
      }
    );
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteOpenChange = (open: boolean) => {
    setIsDeleteOpen(open);
    setDeleteError(null);
  };

  const handleDeleteClick = () => {
    if (!observation) return;

    setDeleteError(null);
    destroyObservation(observation.id, {
      onSuccess: () => {
        setObservation(null);
        setIsDeleteOpen(false);
      },
      onError: () =>
        setDeleteError(
          t(
            'frontend.messages.the_observation_could_not_be_deleted_please_try_again'
          )
        )
    });
  };

  return (
    <>
      <div className="flex-1">
        <Select
          value={observation?.id ? String(observation.id) : null}
          onChange={(key) =>
            setObservation(
              observations.find((gr: ObservationData) => `${gr.id}` === key)
            )
          }
          aria-label={t('frontend.messages.select_observation')}
        >
          <SelectTrigger>
            <SelectValue />
            <SelectIndicator />
          </SelectTrigger>
          <SelectPopover>
            <ListBox items={observations || []}>
              {(obs: ObservationData) => (
                <ListBoxItem
                  id={String(obs.id)}
                  key={obs.id}
                  textValue={obs.display_name}
                >
                  {obs.display_name}
                </ListBoxItem>
              )}
            </ListBox>
          </SelectPopover>
        </Select>
      </div>
      {/* Focus mode drops the sidebar so Scout goes full-bleed. Only shown at
          >=xl, where the sidebar is a persistent column; below that it is
          already a hamburger and toggling focus would do nothing. */}
      <Button
        variant="tertiary"
        isIconOnly
        onPress={toggle}
        aria-pressed={focus}
        aria-label={
          focus
            ? t('frontend.messages.exit_focus_mode')
            : t('frontend.messages.focus_mode')
        }
        className="hidden xl:inline-flex"
      >
        <Icon icon={focus ? 'bi:fullscreen-exit' : 'bi:arrows-fullscreen'} />
      </Button>
      {/* Less-frequent observation actions live in an overflow menu so the
          action bar stays short next to the submit buttons. */}
      <Dropdown>
        <DropdownTrigger>
          <Button
            variant="tertiary"
            isIconOnly
            aria-label={t('frontend.messages.observation_actions')}
          >
            <Icon icon="bi:three-dots-vertical" />
          </Button>
        </DropdownTrigger>
        <DropdownPopover placement="bottom end">
          <DropdownMenu aria-label={t('frontend.messages.observation_actions')}>
            <DropdownItem
              id="new"
              key="new"
              textValue={t('frontend.messages.new_observation')}
              onAction={handleNewClick}
            >
              <span className="flex items-center gap-2">
                <Icon icon="bi:plus" className="size-4" />{' '}
                {t('frontend.messages.new_observation')}
              </span>
            </DropdownItem>
            {observation && (
              <DropdownItem
                id="delete"
                key="delete"
                textValue={t('frontend.messages.delete')}
                onAction={() => setIsDeleteOpen(true)}
              >
                <span className="flex items-center gap-2 text-danger">
                  <Icon icon="bi:trash" className="size-4" />{' '}
                  {t('frontend.messages.delete')}
                </span>
              </DropdownItem>
            )}
          </DropdownMenu>
        </DropdownPopover>
      </Dropdown>
      <AlertDialog isOpen={isDeleteOpen} onOpenChange={handleDeleteOpenChange}>
        <AlertDialog.Backdrop>
          <AlertDialog.Container size="sm">
            <AlertDialog.Dialog>
              <AlertDialog.CloseTrigger />
              <AlertDialog.Header>
                <AlertDialog.Icon status="danger" />
                <AlertDialog.Heading>
                  {t('frontend.messages.delete_observation')}
                </AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                {t('frontend.messages.delete_confirmation', {
                  name: observation?.display_name
                })}
                {deleteError && (
                  <p className="text-danger mt-2">{deleteError}</p>
                )}
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="ghost"
                  onPress={() => handleDeleteOpenChange(false)}
                >
                  {t('frontend.messages.cancel')}
                </Button>
                <Button
                  variant="danger"
                  isPending={isDeleting}
                  onPress={handleDeleteClick}
                >
                  {t('frontend.messages.delete_observation')}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </>
  );
};

const ScoutPage = () => {
  const [observation, setObservation] = useState<ObservationData | null>(null);
  const observer = useUserId();

  return (
    <BaseAuthedPage>
      <div className="flex items-center gap-4">
        <ModelIndexSimple
          analyticsSurface="scout_picker"
          model="observation"
          defaultFilter={{
            observer,
            complete: false
          }}
        >
          <ObservationPicker
            observation={observation}
            setObservation={setObservation}
          />
        </ModelIndexSimple>
        <div
          id="scout-edit-actions"
          className="ml-auto grid grid-cols-2 gap-2"
        />
      </div>
      {observation && (
        <Scout
          key={`game-report-${observation.id}`}
          observation={observation}
        />
      )}
    </BaseAuthedPage>
  );
};

export default ScoutPage;
